import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { referralLabel, REFERRAL_STATUS_DEFS } from '../utils/referralStatus';
import { fmtMoney } from '../utils/format';
import useIsMobile from '../hooks/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const PAGE = 15;
const ACTIVE_BASE_INFO = [
  ['Что считаем', 'Количество НАПРАВЛЕНИЙ (не уникальных пациентов), которые ещё не завершились: не дошли, не признаны дублем и не сгорели по истечении срока. Один и тот же пациент, направленный в 2 клиники — это 2 направления, +2 к активной базе.'],
  ['Освобождение слота', 'Место освобождается сразу, как только направление доходит до одного из трёх исходов: пациент оплатил (дошёл), признан дублем, или сгорел по истечении срока (устанавливается клиникой).'],
  ['При достижении 100%', 'Добавление новых пациентов будет недоступно, пока часть направлений не разрешится визитом или сгоранием.'],
];
const NON_ARRIVED_INFO = [
  ['Что считаем', 'Количество НАПРАВЛЕНИЙ (не уникальных пациентов), сгоревших по истечении срока клиники без единой оплаты. Растёт только от сгораний — не уменьшается визитами или временем.'],
  ['При достижении 100%', 'Кабинет будет заблокирован — добавление новых пациентов станет недоступно. Разблокировать сможет только техподдержка.'],
];
const DAILY_ADD_INFO = [
  ['Что считаем', 'Количество уникальных ПАЦИЕНТОВ, впервые добавленных вами сегодня. Направление уже добавленного сегодня пациента ещё в одну клинику новым добавлением не считается.'],
];
const PAID_TOTAL_INFO = [
  ['Что это', 'Сумма всех оплат, которые пациент внёс в кассу клиники по этому направлению — а не ваша комиссия.'],
  ['Повторные визиты', 'Ежемесячная комиссия с повторных оплат начисляется вам, только если по этой клинике выполнена квота по первичным пациентам за месяц.'],
];
const fmt = fmtMoney;
const fio = (p) => `${p.last_name} ${p.first_name} ${p.middle_name || ''}`.trim();
const dmy = (d) => d ? new Date(d).toLocaleDateString('ru') : '—';

// сводный статус пациента по его целям (приоритет: Дошёл > Подтверждён > Направлен > Дубль > Не дошёл)
function overall(targets) {
  if (!targets || targets.length === 0) return { key: 'none', label: '—', kind: 'gray' };
  if (targets.some(t => t.status === 'primary_done')) return { key: 'reached', label: 'Дошёл', kind: 'teal' };
  if (targets.some(t => t.status === 'active' && t.clinic_confirmed_at)) return { key: 'confirmed', label: 'Подтверждён', kind: 'gray' };
  if (targets.some(t => t.status === 'active')) return { key: 'sent', label: 'Направлен', kind: 'gray' };
  if (targets.some(t => t.status === 'duplicate')) return { key: 'duplicate', label: 'Дубль', kind: 'gray' };
  if (targets.some(t => t.status === 'timed_out')) return { key: 'timed_out', label: 'Не дошёл', kind: 'gray' };
  return { key: 'inactive', label: 'Неактивен', kind: 'gray' };
}

// ближайший (самый срочный) активный таймер среди целей пациента, для столбца «Таймер»
function nearestTimeout(targets) {
  const active = (targets || []).filter(t => t.status === 'active' && t.timeout_at);
  if (active.length === 0) return null;
  return active.reduce((min, t) => (!min || new Date(t.timeout_at) < new Date(min.timeout_at)) ? t : min, null).timeout_at;
}

// N-066: аксессоры для клиентской сортировки столбцов таблицы «Пациенты»
const SORT_ACCESSORS = {
  name: (p) => fio(p).toLowerCase(),
  birth_date: (p) => p.birth_date || '',
  clinics: (p) => (p.targets[0]?.name || '').toLowerCase(),
  status: (p) => overall(p.targets).label,
  timer: (p) => { const t = nearestTimeout(p.targets); return t ? new Date(t).getTime() : Infinity; },
  paid: (p) => p.paid_total || 0,
  created_at: (p) => new Date(p.created_at).getTime(),
};

function TimerCell({ timeoutAt }) {
  if (!timeoutAt) return <span style={s.faint}>—</span>;
  const daysLeft = Math.ceil((new Date(timeoutAt) - new Date()) / 86400000);
  if (daysLeft <= 0) return <span style={s.faint}>истекает</span>;
  const urgent = daysLeft <= 5;
  return <span style={{ color: urgent ? '#e5484d' : '#64748b', fontWeight: urgent ? 700 : 400 }}>{daysLeft} дн.</span>;
}

export default function Patients() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [tab, setTab] = useState('standard'); // N-115: 'standard' | 'operation'
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState(null);
  const [sortField, setSortField] = useState(null); // N-066: клиентская сортировка столбцов
  const [sortDir, setSortDir] = useState('asc');
  const [highlightRef, setHighlightRef] = useState(null); // N-107/108: подсветка направления, открытого по прямой ссылке
  const [copiedRef, setCopiedRef] = useState(null);
  const autoOpened = useRef(false);

  // модалки
  const [editPatient, setEditPatient] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', middle_name: '', birth_date: '' });
  const [editError, setEditError] = useState(''); const [editLoading, setEditLoading] = useState(false);
  const [addFor, setAddFor] = useState(null);
  const [addTargets, setAddTargets] = useState([]); const [addSelected, setAddSelected] = useState([]);
  const [addAlready, setAddAlready] = useState([]); const [addMax, setAddMax] = useState(10);
  const [addError, setAddError] = useState(''); const [addLoading, setAddLoading] = useState(false);
  const tkey = (t) => `${t.target_type}:${t.id}`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios.get(`${API}/patients/`, { headers })
      .then(r => { setPatients(r.data); setLoaded(true); })
      .catch(() => { localStorage.clear(); navigate('/login'); });
    axios.get(`${API}/patients/limits`, { headers }).then(r => setLimits(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { setPage(0); }, [tab]);

  // N-107/108: открыть карточку пациента сразу на нужном направлении по ссылке ?ref=<id направления>
  // (например, продиктованном пациентом/врачом в тикет техподдержки)
  useEffect(() => {
    if (autoOpened.current || !loaded) return;
    const rid = Number(searchParams.get('ref'));
    if (!rid) return;
    const found = patients.find(p => p.targets.some(t => t.referral_id === rid));
    if (found) { setDetail(found); setHighlightRef(rid); }
    autoOpened.current = true;
  }, [loaded, patients, searchParams]);

  const copyRefId = (rid) => {
    const text = `REF-${rid}`;
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
    setCopiedRef(rid);
    setTimeout(() => setCopiedRef(r => (r === rid ? null : r)), 1500);
  };

  // N-115: разделение на «Стандартные»/«Операционные» направления — по вкладке,
  // фильтруем targets КАЖДОГО пациента по типу (не по пациенту целиком — один и тот
  // же пациент может иметь и стандартные, и операционные направления одновременно,
  // к разным целям), пересчитывая сумму оплат под то, что реально показано на вкладке.
  const byTab = useMemo(() => {
    return patients
      .map(p => {
        const targets = (p.targets || []).filter(t => (t.referral_type || 'standard') === tab);
        if (targets.length === 0) return null;
        return { ...p, targets, paid_total: targets.reduce((sum, t) => sum + (t.paid || 0), 0) };
      })
      .filter(Boolean);
  }, [patients, tab]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = byTab.filter(p => {
      if (q && !fio(p).toLowerCase().includes(q)) return false;
      if (status && overall(p.targets).key !== status) return false;
      return true;
    });
    if (!sortField) return rows;
    const acc = SORT_ACCESSORS[sortField];
    const sorted = [...rows].sort((a, b) => {
      const av = acc(a), bv = acc(b);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [byTab, search, status, sortField, sortDir]);

  const toggleSort = (field) => {
    setPage(0);
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortArrow = (field) => (sortField !== field ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice(page * PAGE, page * PAGE + PAGE);

  const canEdit = (editable_until) => {
    if (!editable_until) return false;
    const cleaned = editable_until.replace(' ', 'T').replace(/(\+\d{2}:\d{2})$/, 'Z').replace(/\.\d+Z$/, 'Z');
    return new Date() < new Date(cleaned);
  };

  const openEdit = (p) => {
    setEditPatient(p); setDetail(null);
    setEditForm({ first_name: p.first_name, last_name: p.last_name, middle_name: p.middle_name || '', birth_date: p.birth_date });
    setEditError('');
  };
  const handleEdit = async () => {
    if (!editForm.first_name || !editForm.last_name || !editForm.middle_name || !editForm.birth_date) { setEditError('Заполните все поля'); return; }
    setEditLoading(true);
    try { await axios.patch(`${API}/patients/${editPatient.id}`, editForm, { headers }); setEditPatient(null); load(); }
    catch (e) { setEditError(e.response?.data?.detail || 'Ошибка'); }
    setEditLoading(false);
  };

  const addRemaining = addMax - addAlready.length - addSelected.length;
  const openAdd = async (p) => {
    setAddFor(p); setDetail(null); setAddSelected([]); setAddError('');
    try {
      const r = await axios.get(`${API}/clinics/targets`, { headers, params: { patient_id: p.id } });
      const already = r.data.already_bound.map(tkey);
      setAddAlready(already); setAddMax(r.data.max_per_patient);
      setAddTargets(r.data.targets.filter(t => !already.includes(tkey(t))));
    } catch { setAddError('Не удалось загрузить цели'); }
  };
  const toggleAdd = (t) => {
    const k = tkey(t);
    if (addSelected.includes(k)) { setAddSelected(addSelected.filter(x => x !== k)); setAddError(''); return; }
    if (addRemaining <= 0) { setAddError(`Лимит ${addMax} целей исчерпан`); return; }
    setAddSelected([...addSelected, k]); setAddError('');
  };
  const saveAdd = async () => {
    if (addSelected.length === 0) { setAddError('Выберите хотя бы одну цель'); return; }
    setAddLoading(true);
    try {
      const payload = { patient_id: addFor.id, targets: addSelected.map(k => { const [target_type, id] = k.split(':'); return { target_type, id: Number(id) }; }) };
      const res = await axios.post(`${API}/patients/bind`, payload, { headers });
      if (res.data.errors.length > 0 && res.data.bound.length === 0) setAddError(res.data.errors.map(e => e.error).join(', '));
      else { setAddFor(null); load(); }
    } catch (e) { setAddError(e.response?.data?.detail || 'Ошибка'); }
    setAddLoading(false);
  };

  return (
    <Layout>
      <div style={{ ...s.topRow, ...(isMobile ? s.topRowMobile : {}) }}>
        <h1 style={s.h1}>Пациенты</h1>
        <div style={s.topBtns}>
          <button
            style={{ ...s.btnAdd, ...(limits && limits.verification_status !== 'active' ? s.btnAddDisabled : {}) }}
            onClick={() => { if (limits && limits.verification_status !== 'active') navigate('/profile'); else navigate('/patients/add'); }}
            title={limits && limits.verification_status !== 'active' ? 'Пройдите верификацию ЕГИСЗ в профиле' : ''}
          >
            + Добавить пациента
          </button>
          <button
            style={{ ...s.btnAddOperation, ...(limits && limits.verification_status !== 'active' ? s.btnAddDisabled : {}) }}
            onClick={() => { if (limits && limits.verification_status !== 'active') navigate('/profile'); else navigate('/patients/add?mode=operation'); }}
            title={limits && limits.verification_status !== 'active' ? 'Пройдите верификацию ЕГИСЗ в профиле' : ''}
          >
            + Добавить на операцию
          </button>
        </div>
      </div>
      {/* N-115: две независимые системы направления — обычная (первичные/повторные/квота)
          и «Операция» (свой % и таймер, комиссия с суммы всех оплат за период) */}
      <div style={s.tabs}>
        <button style={{ ...s.tabBtn, ...(tab === 'standard' ? s.tabBtnActive : {}) }} onClick={() => setTab('standard')}>
          Стандартные направления
        </button>
        <button style={{ ...s.tabBtn, ...(tab === 'operation' ? s.tabBtnActive : {}) }} onClick={() => setTab('operation')}>
          Операционные направления
        </button>
      </div>
      {limits && limits.verification_status !== 'active' && (
        <div style={{ ...s.bannerWarn, cursor: 'pointer' }} onClick={() => navigate('/profile')}>
          {limits.verification_status === 'pending'
            ? 'Выписка ЕГИСЗ на проверке — добавление пациентов будет доступно после подтверждения.'
            : limits.verification_status === 'rejected'
              ? `Верификация отклонена${limits.verification_reject_reason ? ': ' + limits.verification_reject_reason : ''}. Загрузите документ повторно в профиле.`
              : 'Загрузите выписку ЕГИСЗ в профиле, чтобы добавлять пациентов.'}
          {' '}Перейти в профиль →
        </div>
      )}
      {limits && limits.blocked && (
        <div style={s.bannerBlocked}>
          <b>Аккаунт заблокирован.</b> {limits.block_reason ? limits.block_reason + '. ' : ''}
          Добавление новых пациентов недоступно. Блокировка не затрагивает расчёт текущих комиссий.
          Вы можете обратиться в техподдержку для разблокировки кабинета.
        </div>
      )}
      {limits && !limits.blocked && thresholdBanner(limits.non_arrived_used, limits.non_arrived_limit,
        'базе недошедших направлений', 'пока техподдержка не сбросит счётчик')}
      {limits && !limits.blocked && thresholdBanner(limits.base_used, limits.base_limit,
        'активной базе направлений', 'пока часть направлений не разрешится визитом или сгоранием')}
      {limits && (
        <div style={{ ...s.limits, ...(isMobile ? s.limitsMobile : {}) }}>
          <LimitBar label="Активная база направлений" used={limits.base_used} max={limits.base_limit} hint="дошедшие/дубли/сгоревшие не учитываются" info={ACTIVE_BASE_INFO} />
          <LimitBar label="База недошедших направлений" used={limits.non_arrived_used} max={limits.non_arrived_limit} hint="растёт только от сгораний" info={NON_ARRIVED_INFO} />
          <LimitBar label="Добавлено пациентов сегодня" used={limits.daily_used} max={limits.daily_limit} hint="считается по пациентам" info={DAILY_ADD_INFO} />
        </div>
      )}
      <div style={{ ...s.filterBar, ...(isMobile ? s.filterBarMobile : {}) }}>
        <input style={{ ...s.search, ...(isMobile ? s.searchMobile : {}) }} placeholder="Поиск пациентов…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
        <select style={s.select} value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}>
          <option value="">Все статусы</option>
          <option value="reached">Дошли</option>
          <option value="confirmed">Подтверждены</option>
          <option value="sent">Направлены</option>
          <option value="duplicate">Дубль</option>
          <option value="timed_out">Не дошли</option>
        </select>
      </div>

      <div style={s.tableCard}>
        <div style={isMobile ? s.scrollX : undefined}>
        <div style={{ minWidth: isMobile ? 760 : 'auto' }}>
        <div style={{ ...s.thead, gridTemplateColumns: cols }}>
          <div style={s.thSortable} onClick={() => toggleSort('name')}>Пациент{sortArrow('name')}</div>
          <div style={s.thSortable} onClick={() => toggleSort('birth_date')}>Дата рождения{sortArrow('birth_date')}</div>
          <div style={s.thSortable} onClick={() => toggleSort('clinics')}>Клиники{sortArrow('clinics')}</div>
          <div style={s.thSortable} onClick={() => toggleSort('status')}>
            Статус{sortArrow('status')}<StatusInfo title="Статусы направления" items={REFERRAL_STATUS_DEFS} />
          </div>
          <div style={s.thSortable} onClick={() => toggleSort('timer')}>Таймер{sortArrow('timer')}</div>
          <div style={s.thSortable} onClick={() => toggleSort('paid')}>Всего оплат{sortArrow('paid')}</div>
          <div style={s.thSortable} onClick={() => toggleSort('created_at')}>Добавлен{sortArrow('created_at')}</div>
        </div>
        {!loaded ? <div style={s.empty}>Загрузка…</div> : pageRows.length === 0 ? (
          <div style={s.empty}>
            {byTab.length === 0
              ? (tab === 'operation' ? 'У вас пока нет направлений на операции' : 'У вас пока нет пациентов')
              : 'Ничего не найдено'}
          </div>
        ) : pageRows.map(p => {
          const ov = overall(p.targets);
          const first = p.targets[0];
          return (
            <div key={p.id} style={{ ...s.row, gridTemplateColumns: cols }} onClick={() => setDetail(p)}>
              <div style={s.nameCell}>
                <div style={s.avatar}>{p.last_name[0]}{p.first_name[0]}</div>
                <span style={s.strong}>{fio(p)}</span>
              </div>
              <div>{dmy(p.birth_date)}</div>
              <div style={s.faint}>{first ? `${first.target_type === 'network' ? '🏥 ' : ''}${first.name}${p.targets.length > 1 ? ` +${p.targets.length - 1}` : ''}` : '—'}</div>
              <div><span style={{ ...s.badge, ...(ov.kind === 'teal' ? s.badgeTeal : s.badgeGray) }}>{ov.label}</span></div>
              <div><TimerCell timeoutAt={nearestTimeout(p.targets)} /></div>
              <div style={s.money}>{fmt(p.paid_total)} ₽</div>
              <div style={s.faint}>{dmy(p.created_at)}</div>
            </div>
          );
        })}
        </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={s.pager}>
          <span>{page * PAGE + 1}–{Math.min((page + 1) * PAGE, filtered.length)} из {filtered.length}</span>
          <div style={s.pagerBtns}>
            <button style={{ ...s.pBtn, ...(page <= 0 ? s.pDis : {}) }} onClick={() => page > 0 && setPage(page - 1)} disabled={page <= 0}>← Назад</button>
            <span style={s.faint}>{page + 1} / {totalPages}</span>
            <button style={{ ...s.pBtn, ...(page >= totalPages - 1 ? s.pDis : {}) }} onClick={() => page < totalPages - 1 && setPage(page + 1)} disabled={page >= totalPages - 1}>Вперёд →</button>
          </div>
        </div>
      )}

      {/* Карточка пациента */}
      {detail && (
        <div style={s.overlay} onClick={() => setDetail(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.detailHead}>
              <div style={s.avatarLg}>{detail.last_name[0]}{detail.first_name[0]}</div>
              <div>
                <div style={s.detailName}>{fio(detail)}</div>
                <div style={s.faint}>{dmy(detail.birth_date)}</div>
              </div>
            </div>
            <div style={s.paidCard}>
              <div style={s.paidLabel}>Всего оплат пациентом по направлению за весь период<StatusInfo items={PAID_TOTAL_INFO} /></div>
              <div style={s.paidValue}>{fmt(detail.paid_total)} ₽</div>
            </div>
            <div style={s.detailSection}>Направления ({detail.targets.length})</div>
            <div style={s.targetsList}>
              {detail.targets.map(t => (
                <div key={t.referral_id} style={{ ...s.targetCard, ...(t.referral_id === highlightRef ? s.targetCardHl : {}) }}>
                  <div style={s.targetTop}>
                    <span style={s.targetName}>{t.target_type === 'network' ? '🏥 ' : ''}{t.name}</span>
                    <span style={{ ...s.badge, ...(t.status === 'primary_done' ? s.badgeTeal : s.badgeGray) }}>{referralLabel(t.status, t.clinic_confirmed_at)}</span>
                  </div>
                  <div style={s.refIdRow}>
                    <span style={s.refIdLabel}>ID направления</span>
                    <span style={s.refIdValue}>REF-{t.referral_id}</span>
                    <button style={s.copyBtn} onClick={() => copyRefId(t.referral_id)}>
                      {copiedRef === t.referral_id ? 'Скопировано ✓' : 'Скопировать'}
                    </button>
                  </div>
                  <ReferralStepper t={t} />
                  <ReferralHistory t={t} />
                  {t.paid > 0 && <div style={s.targetPaid}>Оплачено: <b>{fmt(t.paid)} ₽</b></div>}
                </div>
              ))}
            </div>
            <div style={s.detailActions}>
              {detail.targets.length < 3 && <button style={s.btnAdd} onClick={() => openAdd(detail)}>+ Добавить клиники</button>}
              {canEdit(detail.editable_until)
                ? <button style={s.btnGhost} onClick={() => openEdit(detail)}>Редактировать ФИО</button>
                : <span style={s.locked}>🔒 ФИО закрыто для правок</span>}
              <button style={s.btnGhost} onClick={() => setDetail(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {/* Редактирование ФИО */}
      {editPatient && (
        <div style={s.overlay} onClick={() => setEditPatient(null)}>
          <div style={s.modalSm} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Редактировать пациента</h3>
            <div style={s.modalRow}>
              <input style={s.input} placeholder="Фамилия *" value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} />
              <input style={s.input} placeholder="Имя *" value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} />
            </div>
            <input style={s.input} placeholder="Отчество *" value={editForm.middle_name} onChange={e => setEditForm({ ...editForm, middle_name: e.target.value })} />
            <input style={s.input} type="date" value={editForm.birth_date} onChange={e => setEditForm({ ...editForm, birth_date: e.target.value })} />
            {editError && <div style={s.error}>{editError}</div>}
            <div style={s.modalBtns}>
              <button style={s.btnGhost} onClick={() => setEditPatient(null)}>Отмена</button>
              <button style={s.btnAdd} onClick={handleEdit} disabled={editLoading}>{editLoading ? 'Сохранение…' : 'Сохранить'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Добавить клиники */}
      {addFor && (
        <div style={s.overlay} onClick={() => setAddFor(null)}>
          <div style={s.modalSm} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Добавить клиники</h3>
            <div style={s.strong}>{fio(addFor)}</div>
            <div style={s.faint}>Выбрано: {addSelected.length}{addAlready.length ? ` (+${addAlready.length} привязано)` : ''} · осталось {Math.max(addRemaining, 0)} из {addMax}.</div>
            {addTargets.length === 0 ? <div style={s.empty}>Нет доступных целей</div> : (
              <div style={s.addList}>
                {addTargets.map(t => {
                  const k = tkey(t); const isSel = addSelected.includes(k);
                  return (
                    <div key={k} style={{ ...s.addItem, ...(isSel ? s.addItemSel : {}) }} onClick={() => toggleAdd(t)}>
                      <div style={s.addCheck}>{isSel ? '☑' : '☐'}</div>
                      <div>
                        <div style={s.addName}>{t.name}
                          <span style={t.target_type === 'network' ? s.tagNet : s.tagClinic}>{t.target_type === 'network' ? `сеть · ${t.member_count} учр.` : 'клиника'}</span>
                        </div>
                        <div style={s.faint}>{t.commission_primary_pct != null ? `Первичный: ${t.commission_primary_pct}% · Повторный: ${t.commission_repeat_pct}% · Квота: ${t.monthly_quota}/мес` : 'Условия ещё не заданы'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {addError && <div style={s.error}>{addError}</div>}
            <div style={s.modalBtns}>
              <button style={s.btnGhost} onClick={() => setAddFor(null)}>Отмена</button>
              <button style={s.btnAdd} onClick={saveAdd} disabled={addLoading || addSelected.length === 0}>{addLoading ? 'Сохранение…' : `Добавить (${addSelected.length})`}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// N-049: предупреждения на 50%/90% лимита (оранжевое/красное), для активной базы
// и для базы недошедших направлений — не блокирует, только предупреждает заранее.
function thresholdBanner(used, max, name, resolution) {
  if (!max) return null;
  const pct = used / max;
  if (pct < 0.5) return null;
  const style = pct >= 0.9 ? s.bannerBlocked : s.bannerWarn;
  const pctLabel = pct >= 0.9 ? '90%' : '50%';
  return <div style={style}>Вы использовали {pctLabel} лимита в {name} ({used} из {max}). При достижении 100% добавление новых пациентов будет недоступно, {resolution}.</div>;
}

function LimitBar({ label, used, max, hint, info }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const color = used >= max ? '#e5484d' : pct >= 80 ? '#f59e0b' : '#2EC4B6';
  return (
    <div style={s.limitCard}>
      <div style={s.limitTop}>
        <span style={s.limitLabel}>{label}{info && <StatusInfo items={info} />}</span>
        <span style={{ ...s.limitVal, color }}>{used} / {max}</span>
      </div>
      <div style={s.limitTrack}><div style={{ ...s.limitFill, width: `${pct}%`, background: color }} /></div>
      <div style={s.limitHint}>{hint}</div>
    </div>
  );
}

// N-107/108: горизонтальный степпер по трём укрупнённым фазам направления —
// компактный обзор, куда докатилось направление и когда. Финальный шаг отражает
// реальный исход (Дошёл/Дубль/Не дошёл), а не всегда «Дошёл».
function ReferralStepper({ t }) {
  const steps = [
    { label: 'Направлен', date: t.bound_at, done: !!t.bound_at, fail: false },
    { label: 'Подтверждён', date: t.clinic_confirmed_at, done: !!t.clinic_confirmed_at, fail: false },
  ];
  if (t.status === 'primary_done') steps.push({ label: 'Дошёл', date: t.primary_visit_at, done: true, fail: false });
  else if (t.status === 'duplicate') steps.push({ label: 'Дубль', date: null, done: true, fail: true });
  else if (t.status === 'timed_out') steps.push({ label: 'Не дошёл', date: t.timeout_at, done: true, fail: true });
  else steps.push({ label: 'Дошёл', date: null, done: false, fail: false });

  const nodes = [];
  steps.forEach((st, i) => {
    if (i > 0) nodes.push(<div key={`c${i}`} style={{ ...s.stepConnector, background: steps[i - 1].done ? '#2EC4B6' : '#e2e8f0' }} />);
    nodes.push(
      <div key={i} style={s.stepCol}>
        <div style={{ ...s.stepDot, ...(st.done ? (st.fail ? s.stepDotFail : s.stepDotDone) : {}) }}>{st.done ? (st.fail ? '✕' : '✓') : ''}</div>
        <div style={{ ...s.stepLabel, ...(st.fail ? { color: '#e5484d' } : {}) }}>{st.label}</div>
        <div style={s.stepDate}>{st.date ? new Date(st.date).toLocaleDateString('ru') : '—'}</div>
      </div>
    );
  });
  return <div style={s.stepperRow}>{nodes}</div>;
}

// N-108: разворачиваемая «История изменений» — полная детализация по датам,
// включая каждую отдельную оплату (Первая/Повторная ×N с суммой), свёрнута по умолчанию.
function ReferralHistory({ t }) {
  const [open, setOpen] = useState(false);
  const payments = t.payments || [];
  const steps = [
    { label: 'Направлен', date: t.bound_at },
    { label: 'Подтверждён клиникой', date: t.clinic_confirmed_at },
  ];
  if (payments.length > 0) {
    payments.forEach((p, i) => {
      steps.push({ label: i === 0 ? 'Первая оплата' : 'Повторная оплата', date: p.date, amount: p.amount });
    });
  } else if (t.status === 'primary_done') {
    steps.push({ label: 'Первая оплата', date: t.primary_visit_at });
  } else if (t.status === 'duplicate') {
    steps.push({ label: 'Признан дублем', date: null });
  } else if (t.status === 'timed_out') {
    steps.push({ label: 'Срок истёк, направление снято', date: t.timeout_at });
  }
  return (
    <div style={s.historyWrap}>
      <button style={s.historyToggle} onClick={() => setOpen(o => !o)}>
        История изменений {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={s.timeline}>
          {steps.map((st, i) => (
            <div key={i} style={s.tlStep}>
              <div style={{ ...s.tlDot, ...(st.date ? s.tlDotDone : {}) }}>{st.date ? '✓' : ''}</div>
              <div style={s.tlText}>
                <span style={{ ...s.tlLabel, ...(st.date ? {} : { color: '#94a3b8' }) }}>{st.label}</span>
                <span style={s.tlAmount}>{st.amount != null ? `${fmt(st.amount)} ₽` : ''}</span>
                <span style={s.tlDate}>{st.date ? new Date(st.date).toLocaleDateString('ru') : '—'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const cols = '2fr 1.2fr 1.6fr 1fr 0.8fr 1fr 1fr';
const s = {
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  topRowMobile: { flexWrap: 'wrap', gap: 12 },
  h1: { color: '#1B2B4B', fontSize: 28, fontWeight: 800, margin: 0 },
  topBtns: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  btnAdd: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnAddOperation: { background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnAddDisabled: { background: '#94a3b8', color: '#fff', borderColor: '#94a3b8' },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tabBtn: { background: '#fff', color: '#64748b', border: '1px solid #d7dee8', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  tabBtnActive: { background: '#1B2B4B', color: '#fff', borderColor: '#1B2B4B' },
  btnGhost: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 16px', fontSize: 14, cursor: 'pointer' },
  bannerBlocked: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#b42318', borderRadius: 12, padding: '14px 18px', fontSize: 14, marginBottom: 16, lineHeight: 1.5 },
  bannerWarn: { background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 12, padding: '14px 18px', fontSize: 13, marginBottom: 16, lineHeight: 1.5 },
  limits: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 },
  limitsMobile: { gridTemplateColumns: '1fr' },
  limitCard: { background: '#fff', borderRadius: 14, padding: '14px 18px', boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  limitTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 },
  limitLabel: { fontSize: 13, color: '#64748b', fontWeight: 500 },
  limitVal: { fontSize: 16, fontWeight: 800 },
  limitTrack: { height: 6, borderRadius: 3, background: '#eef2f7', overflow: 'hidden' },
  limitFill: { height: '100%', borderRadius: 3, transition: 'width .3s' },
  limitHint: { fontSize: 11, color: '#94a3b8', marginTop: 6 },
  filterBar: { display: 'flex', gap: 10, marginBottom: 20 },
  filterBarMobile: { flexDirection: 'column' },
  search: { flex: 1, maxWidth: 360, background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none' },
  searchMobile: { maxWidth: 'none' },
  select: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none' },
  tableCard: { background: '#fff', borderRadius: 16, padding: '8px 24px', boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  scrollX: { overflowX: 'auto' },
  thead: { display: 'grid', padding: '16px 4px 12px', color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7' },
  thSortable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '12px 4px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center', cursor: 'pointer' },
  nameCell: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: { width: 36, height: 36, borderRadius: 18, background: '#e9faf7', color: '#149e90', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  strong: { fontWeight: 600 },
  faint: { color: '#94a3b8' },
  money: { color: '#149e90', fontWeight: 700 },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'inline-block' },
  badgeTeal: { background: '#e9faf7', color: '#149e90' },
  badgeGray: { background: '#f1f5f9', color: '#64748b' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '32px 0', fontSize: 14 },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px', color: '#64748b', fontSize: 13 },
  pagerBtns: { display: 'flex', alignItems: 'center', gap: 12 },
  pBtn: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer' },
  pDis: { opacity: 0.4, cursor: 'default' },
  // overlay/modals
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 20, padding: 28, width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(15,23,42,0.3)' },
  modalSm: { background: '#fff', borderRadius: 20, padding: 28, width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 60px rgba(15,23,42,0.3)' },
  detailHead: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 },
  avatarLg: { width: 56, height: 56, borderRadius: 28, background: '#2EC4B6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700 },
  detailName: { fontSize: 19, fontWeight: 700, color: '#1B2B4B' },
  paidCard: { background: '#f6f8fb', borderRadius: 14, padding: 18, marginBottom: 18 },
  paidLabel: { fontSize: 12, color: '#64748b', marginBottom: 4 },
  paidValue: { fontSize: 26, fontWeight: 800, color: '#1B2B4B' },
  detailSection: { fontSize: 14, fontWeight: 700, color: '#1B2B4B', marginBottom: 10 },
  targetsList: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 },
  targetCard: { border: '1px solid #eef2f7', borderRadius: 14, padding: 16 },
  targetCardHl: { border: '2px solid #2EC4B6', boxShadow: '0 0 0 4px #e9faf7' },
  targetTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  targetName: { fontSize: 15, fontWeight: 600, color: '#1B2B4B' },
  targetPaid: { fontSize: 13, color: '#64748b', marginTop: 10 },
  refIdRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 },
  refIdLabel: { fontSize: 11, color: '#94a3b8' },
  refIdValue: { fontSize: 12, fontWeight: 700, color: '#1B2B4B', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: '#f6f8fb', padding: '2px 8px', borderRadius: 6 },
  copyBtn: { marginLeft: 'auto', background: 'none', border: '1px solid #d7dee8', borderRadius: 8, color: '#149e90', fontSize: 11, fontWeight: 600, padding: '4px 9px', cursor: 'pointer' },
  stepperRow: { display: 'flex', alignItems: 'flex-start', marginBottom: 6 },
  stepCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 84, flexShrink: 0 },
  stepConnector: { height: 2, flex: 1, marginTop: 11, minWidth: 8 },
  stepDot: { width: 22, height: 22, borderRadius: 11, background: '#f1f5f9', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 },
  stepDotDone: { background: '#2EC4B6' },
  stepDotFail: { background: '#e5484d' },
  stepLabel: { fontSize: 11.5, fontWeight: 600, color: '#1B2B4B', textAlign: 'center', marginTop: 6, lineHeight: 1.25 },
  stepDate: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  historyWrap: { marginTop: 6, marginBottom: 4 },
  historyToggle: { background: 'none', border: 'none', color: '#149e90', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 0' },
  timeline: { display: 'flex', flexDirection: 'column', gap: 0, marginTop: 10 },
  tlStep: { display: 'flex', gap: 12, alignItems: 'flex-start', paddingBottom: 12, position: 'relative' },
  tlDot: { width: 22, height: 22, borderRadius: 11, background: '#f1f5f9', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, zIndex: 1 },
  tlDotDone: { background: '#2EC4B6' },
  tlText: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, flex: 1, alignItems: 'center' },
  tlLabel: { fontSize: 13, color: '#1B2B4B', fontWeight: 500 },
  tlAmount: { fontSize: 13, color: '#149e90', fontWeight: 600, textAlign: 'right', minWidth: 70 },
  tlDate: { fontSize: 12, color: '#94a3b8', textAlign: 'right' },
  detailActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
  locked: { fontSize: 12, color: '#94a3b8', background: '#f1f5f9', padding: '8px 12px', borderRadius: 20 },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#1B2B4B', margin: 0 },
  modalRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  input: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  modalBtns: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 4 },
  addList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' },
  addItem: { display: 'flex', gap: 12, padding: '12px 14px', border: '1px solid #eef2f7', borderRadius: 12, cursor: 'pointer', alignItems: 'center' },
  addItemSel: { border: '2px solid #2EC4B6', background: '#f0fffe' },
  addCheck: { fontSize: 20, color: '#2EC4B6', flexShrink: 0 },
  addName: { fontSize: 14, fontWeight: 600, color: '#1B2B4B', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  tagNet: { fontSize: 11, fontWeight: 600, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: 20 },
  tagClinic: { fontSize: 11, fontWeight: 600, color: '#149e90', background: '#e9faf7', padding: '2px 8px', borderRadius: 20 },
};
